import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
} from "@mui/material";
import { LayoutProps } from "../types";
import { useAppContext } from "../context/AppContext";
import ApiService from "../services/api";
import { useNavigation } from "./Layout/hooks/useNavigation";
import { AppBar } from "./Layout/components/AppBar";
import { Snackbar } from "./Layout/components/Snackbar";

const Layout: React.FC<LayoutProps> = ({
  children,
  showBackButton = true,
  mode = "scroll",
  scroll,
}) => {
  const { snackbar, setSnackbar, currentView, setCurrentView, clearAppState, userId, token, sessionId, role, getTranslation, isRTL } =
    useAppContext();

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Language menu state
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const languageMenuOpen = Boolean(languageMenuAnchor);

  // LanguageSelector expansion state
  const [isLanguageSelectorExpanded, setIsLanguageSelectorExpanded] = useState(false);
  const languageSelectorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isScrollable = scroll ?? mode === "scroll";

  // Close LanguageSelector when clicking outside or when language is selected
  useEffect(() => {
    if (!isLanguageSelectorExpanded) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't collapse if clicking on the LanguageSelector, its input, or its dropdown menu
      if (
        languageSelectorRef.current &&
        !languageSelectorRef.current.contains(target) &&
        !target.closest('.MuiPaper-root') &&
        !target.closest('[role="listbox"]') &&
        !target.closest('.MuiTextField-root') &&
        !target.closest('.MuiInputBase-root')
      ) {
        setIsLanguageSelectorExpanded(false);
      }
    };

    // Use a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageSelectorExpanded]);

  const handleLanguageChange = () => {
    setIsLanguageSelectorExpanded(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    // For signup/contact views, show language dropdown directly
    // For other views, show the slide-in menu panel
    if (currentView === "signup" || currentView === "contact") {
      // Don't open menu, language dropdown will be shown separately
      return;
    }
    // Open language panel directly when menu button is clicked
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // Close the main menu and open language panel immediately
    handleMenuClose();
    setLanguageMenuAnchor(anchorEl);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  /**
   * Handle user sign out
   * 
   * This function performs a complete sign out by:
   * 1. Clearing all sessionStorage (temporary session data)
   * 2. Clearing all cookies (persistent data including):
   *    - token (authentication token)
   *    - userId (user identifier)
   *    - selectedLanguage (user's language preference)
   *    - Any other stored data
   * 3. Clearing all app state via clearAppState()
   * 4. Closing the menu
   * 5. Calling the server-side sign out API
   */
  const handleSignOut = async () => {
    // Clear all storage
    sessionStorage.clear();
    
    // Clear app state (this broadcasts logout to other tabs AND clears cookies for both flows)
    clearAppState();
    handleMenuClose();
    
    try {
        const api = new ApiService(sessionId, token);
        await api.signOut();
    } catch (error) {
      console.error('Sign out API call failed:', error);
    }
  };

  const { handleBack } = useNavigation({
    currentView,
    userId,
    setCurrentView,
  });

  // Scroll only the content area to top on view change.
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [currentView]);

  return (
    <Box
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        height: "100dvh",
        backgroundColor: "#F9F8F4",
        width: "100%",
        overflow: "hidden",
      }}
      className="layout"
    >
      <AppBar
        showBackButton={showBackButton}
        currentView={currentView}
        role={role}
        userId={userId}
        isRTL={isRTL}
        getTranslation={getTranslation}
        handleBack={handleBack}
        anchorEl={anchorEl}
        open={open}
        languageMenuOpen={languageMenuOpen}
        handleMenuClick={handleMenuClick}
        handleMenuClose={handleMenuClose}
        handleLanguageMenuClick={handleLanguageMenuClick}
        handleLanguageMenuClose={handleLanguageMenuClose}
        handleLanguageChange={handleLanguageChange}
        handleSignOut={handleSignOut}
        setCurrentView={setCurrentView}
      />

      <Container
        ref={contentRef}
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          paddingTop: {
            xs: "50px",  // AppBar height (64px) + extra spacing
            sm: "50px",
            md: "50px",
            lg: "50px",
            xl: "50px"
          },
          paddingBottom: isScrollable ? "24px" : "0px",
          paddingLeft: {
            xs: "20px",
            sm: "20px",
            md: "20px",
            lg: "20px",
            xl: "20px"
          },
          paddingRight: "20px",
          maxWidth: { lg: "100%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          overflowX: "hidden",
          overflowY: isScrollable ? "auto" : "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {children}
        </Box>
      </Container>

      <Snackbar snackbar={snackbar} setSnackbar={setSnackbar} />
    </Box>
  );
};

export default Layout;
